import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  goChatService,
  goChatSocket,
  ensureChatTokenForUser,
} from '../services/goChatApi';
import { productService, reservationService } from '../services/api';
import { useTranslation } from 'react-i18next';
import { formatDate as formatLocaleDate, formatTime as formatLocaleTime } from '../utils/i18n';

import ConversationList from '../components/GoMessages/ConversationList';
import MessageThread from '../components/GoMessages/MessageThread';
import useConversationEnrichment from '../components/GoMessages/useConversationEnrichment';

/**
 * Página /go-messages. Modelo nuevo: convos 1-a-1 entre usuarios, sin
 * producto/reserva de por medio en la convo, pero un mensaje individual
 * puede referenciar un producto (chat iniciado desde "Contactar vendedor")
 * o una reserva (chat iniciado post-reserva o desde Mis compraventas).
 *
 * URL params:
 *   ?userId=X         – abrir/crear convo con el user X
 *   ?productId=Y      – chip "Producto adjunto" para el primer mensaje
 *   ?reservationId=Z  – chip "Reserva adjunta" para el primer mensaje
 *                       (mutuamente excluyente con productId; el user
 *                       puede deseleccionarlo antes de enviar).
 */
const GoMessages = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isPhoneLayout, setIsPhoneLayout] = useState(() => window.matchMedia('(max-width:799px)').matches);

  useEffect(() => {
    const query = window.matchMedia('(max-width:799px)');
    const onChange = (e) => setIsPhoneLayout(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  // Producto / reserva adjunta al próximo mensaje (chip arriba del input).
  // Mutuamente excluyentes — vienen del query param ?productId= o
  // ?reservationId=; el user puede limpiarlos antes de enviar y quedan en
  // null para los mensajes siguientes.
  const [attachedProductId, setAttachedProductId] = useState(null);
  const [attachedReservationId, setAttachedReservationId] = useState(null);
  // Caches para renderear las tarjetas dentro de mensajes y el chip
  // adjunto. Se llenan bajo demanda con productService / reservationService.
  const [productCache, setProductCache] = useState({});
  const [reservationCache, setReservationCache] = useState({});

  const messagesEndRef = useRef(null);
  const activeConvRef = useRef(null);

  const { users, enrichConversations } = useConversationEnrichment(String(user?.id));

  useEffect(() => {
    activeConvRef.current = activeConv;
  }, [activeConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (conversations.length > 0) {
      enrichConversations(conversations);
    }
  }, [conversations, enrichConversations]);

  // Helper: pedir un producto al cache (si ya está, no fetch). Llamado tanto
  // desde el efecto del attachedProductId como cuando aparecen mensajes con
  // payload.productId que todavía no enriquecimos.
  const ensureProduct = useCallback((productId) => {
    if (!productId) return;
    const key = String(productId);
    if (productCache[key]) return;
    productService.getProductById(key)
      .then((res) => {
        const data = res.data?.data ?? res.data;
        if (data) setProductCache((prev) => ({ ...prev, [key]: data }));
      })
      .catch(() => {});
  }, [productCache]);

  // Mismo patrón para reservas (payload.reservationId). El endpoint devuelve
  // la reserva (productId, status, finalPrice, etc.); para mostrar imagen y
  // nombre del producto del lado UI también pre-cacheamos su producto.
  const ensureReservation = useCallback((reservationId) => {
    if (!reservationId) return;
    const key = String(reservationId);
    if (reservationCache[key]) return;
    reservationService.getReservationById(key)
      .then((res) => {
        const data = res.data?.data ?? res.data;
        if (!data) return;
        setReservationCache((prev) => ({ ...prev, [key]: data }));
        if (data.productId) ensureProduct(data.productId);
      })
      .catch(() => {});
  }, [reservationCache, ensureProduct]);

  useEffect(() => {
    if (attachedProductId) ensureProduct(attachedProductId);
  }, [attachedProductId, ensureProduct]);

  useEffect(() => {
    if (attachedReservationId) ensureReservation(attachedReservationId);
  }, [attachedReservationId, ensureReservation]);

  // Cuando llegan mensajes con productId/reservationId, asegurar que estén cacheados.
  useEffect(() => {
    const productIds = [...new Set(messages.map((m) => m.payload?.productId).filter(Boolean))];
    productIds.forEach(ensureProduct);
    const reservationIds = [...new Set(messages.map((m) => m.payload?.reservationId).filter(Boolean))];
    reservationIds.forEach(ensureReservation);
  }, [messages, ensureProduct, ensureReservation]);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const otherParticipantId = useCallback((conv) => {
    if (!conv?.participants) return null;
    const me = String(user?.id);
    return conv.participants.find((id) => String(id) !== me) || null;
  }, [user?.id]);

  const getOtherParticipantName = useMemo(() => (conv) => {
    const otherId = otherParticipantId(conv);
    if (!otherId) return t('Usuario');
    const cached = users[String(otherId)];
    if (cached?.name) return cached.name;
    return t('Usuario #{{id}}', { id: otherId });
  }, [users, otherParticipantId, t]);

  const formatTime = useCallback((dateString) => {
    return formatLocaleTime(dateString, { options: { hour: '2-digit', minute: '2-digit' } });
  }, []);

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return t('Hoy');
    if (date.toDateString() === yesterday.toDateString()) return t('Ayer');
    return formatLocaleDate(dateString, { options: { day: 'numeric', month: 'short' } });
  }, [t]);

  const buildVirtualConv = useCallback((otherId) => ({
    id: `pending-${otherId}`,
    participants: [String(user?.id), String(otherId)],
    lastMessage: '',
    updatedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
    _pending: true,
    _otherUserId: String(otherId),
  }), [user?.id]);

  // ─── WebSocket initialisation ─────────────────────────────────────────────
  // Gateado por user?.id: la auth context puede tardar un render extra en
  // poblarse y si arrancamos antes, las comparaciones contra user?.id
  // toman "undefined" → falsean checks de self-chat y la convo virtual
  // se crea con el propio user (rompía con "Cannot start a chat with
  // yourself" al enviar).
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;

    const init = async () => {
      try {
        // Validar / refrescar el chat token contra el user actual ANTES
        // de cualquier llamada. Si quedó un token de una sesión previa
        // (otro user), Go nos autenticaría con ese userId y rompería con
        // "Cannot start a chat with yourself" al intentar abrir convo
        // con quien creemos que es otro user pero según Go somos nosotros.
        await ensureChatTokenForUser(user.id);
        if (cancelled) return;
        await goChatSocket.connect();
        if (cancelled) return;
        setIsConnected(true);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(t('No se pudo conectar al chat. Verifica que el servicio esté corriendo.'));
        }
      }

      if (cancelled) return;

      try {
        const response = await goChatService.getUserConversations();
        if (cancelled) return;
        const conversationsData = response.data.conversations || [];
        setConversations(conversationsData);

        const targetUserId = searchParams.get('userId') || searchParams.get('sellerId');
        const productIdParam = searchParams.get('productId');
        const reservationIdParam = searchParams.get('reservationId');
        // reservationId tiene prioridad — son mutuamente excluyentes en URL.
        if (reservationIdParam) {
          setAttachedReservationId(String(reservationIdParam));
          setAttachedProductId(null);
        } else if (productIdParam) {
          setAttachedProductId(String(productIdParam));
          setAttachedReservationId(null);
        }

        if (targetUserId && String(targetUserId) !== String(user?.id)) {
          const existing = conversationsData.find((c) =>
            c.participants && c.participants.includes(String(targetUserId))
          );
          if (existing) {
            setActiveConv(existing);
            const msgResponse = await goChatService.getConversationMessages(existing.id);
            if (cancelled) return;
            setMessages(msgResponse.data.messages || []);
            goChatService.markAsRead(existing.id).catch(() => {});
          } else {
            const virtualConv = buildVirtualConv(targetUserId);
            setActiveConv(virtualConv);
            setMessages([]);
            enrichConversations([virtualConv]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(t('Error al cargar las conversaciones'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    init();

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
      goChatSocket.disconnect();
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── WebSocket event listeners (runs once) ──────────────────────────────────

  useEffect(() => {
    const handleConnected = () => { setIsConnected(true); setError(null); };
    const handleClose = () => setIsConnected(false);

    const handleIncomingMessage = (data) => {
      if (data.message && activeConvRef.current && data.conversationId === activeConvRef.current.id) {
        setMessages(prev => [...prev, data.message]);
        goChatService.markAsRead(activeConvRef.current.id).catch(() => {});
      }

      if (data.conversation) {
        setConversations(prev => {
          const exists = prev.some(r => r.id === data.conversation.id);
          if (exists) {
            return prev.map(r =>
              r.id === data.conversation.id
                ? { ...r, ...data.conversation, hasUnread: r.id !== activeConvRef.current?.id }
                : r
            );
          }
          return [{ ...data.conversation, hasUnread: true }, ...prev];
        });
        if (activeConvRef.current && data.conversation.id === activeConvRef.current.id) {
          setActiveConv(prev => ({ ...prev, ...data.conversation }));
        }
      } else if (data.conversationId && data.message) {
        setConversations(prev => prev.map(r =>
          r.id === data.conversationId ? {
            ...r,
            lastMessage: data.message.payload?.text || r.lastMessage,
            updatedAt: data.message.createdAt,
            hasUnread: r.id !== activeConvRef.current?.id,
          } : r
        ));
      }
    };

    const handleError = (data) => setError(data.error);

    goChatSocket.on('CONNECTED', handleConnected);
    goChatSocket.on('close', handleClose);
    goChatSocket.on('MESSAGE', handleIncomingMessage);
    goChatSocket.on('ERROR', handleError);

    return () => {
      goChatSocket.off('CONNECTED', handleConnected);
      goChatSocket.off('close', handleClose);
      goChatSocket.off('MESSAGE', handleIncomingMessage);
      goChatSocket.off('ERROR', handleError);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reaccionar a cambios de URL (navegación dentro de /messages: ej. de un
  // chat con user A a otro con user B). El init effect arriba sólo corre al
  // montar; sin esto los params nuevos se ignoran y queda el activeConv y
  // attachedProductId stale.
  useEffect(() => {
    if (loading || !user?.id) return;
    const targetUserId = searchParams.get('userId') || searchParams.get('sellerId');
    const productIdParam = searchParams.get('productId');
    const reservationIdParam = searchParams.get('reservationId');

    // Adjunto: lo igualamos al param. reservationId pisa a productId.
    // Si la URL no trae ninguno, limpiamos ambos chips.
    if (reservationIdParam) {
      setAttachedReservationId(String(reservationIdParam));
      setAttachedProductId(null);
    } else if (productIdParam) {
      setAttachedProductId(String(productIdParam));
      setAttachedReservationId(null);
    } else {
      setAttachedProductId(null);
      setAttachedReservationId(null);
    }

    if (!targetUserId || String(targetUserId) === String(user?.id)) return;

    // Si la convo activa ya apunta al mismo user, no hacemos nada — sólo el
    // chip de producto se actualizó arriba.
    const isAlreadyOpen = activeConv && (
      activeConv._otherUserId === String(targetUserId)
      || (activeConv.participants && activeConv.participants.includes(String(targetUserId)))
    );
    if (isAlreadyOpen) return;

    // Cambió el target — abrir convo existente o crear virtual.
    const existing = conversations.find((c) =>
      c.participants && c.participants.includes(String(targetUserId))
    );
    if (existing) {
      setActiveConv(existing);
      setMessages([]);
      goChatService.getConversationMessages(existing.id)
        .then((res) => setMessages(res.data.messages || []))
        .catch(() => setError(t('Error al cargar mensajes')));
      goChatService.markAsRead(existing.id).catch(() => {});
    } else {
      const virtualConv = buildVirtualConv(targetUserId);
      setActiveConv(virtualConv);
      setMessages([]);
      enrichConversations([virtualConv]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user?.id, loading]);

  // ─── Conversation / message actions ─────────────────────────────────────────

  const openConversation = useCallback(async (conv) => {
    setActiveConv(conv);
    setMessages([]);
    // Cambiar de convo limpia los adjuntos — sólo aplican al flow inicial
    // de "iniciar desde producto/reserva".
    setAttachedProductId(null);
    setAttachedReservationId(null);

    try {
      const response = await goChatService.getConversationMessages(conv.id);
      setMessages(response.data.messages || []);
      goChatService.markAsRead(conv.id).catch(() => {});
      setConversations(prev => prev.map(r =>
        r.id === conv.id ? { ...r, hasUnread: false } : r
      ));
    } catch (err) {
      setError(t('Error al cargar mensajes'));
    }
  }, [t]);

  const handleSendMessage = useCallback(async () => {
    if (!activeConv || !isConnected) return;
    const text = inputText.trim();
    const productIdAttachment = attachedProductId;
    const reservationIdAttachment = attachedReservationId;
    // Permitir enviar solo adjunto sin texto, o solo texto sin adjunto.
    if (!text && !productIdAttachment && !reservationIdAttachment) return;

    setInputText('');
    setAttachedProductId(null);
    setAttachedReservationId(null);

    // Convo virtual: el primer mensaje gatilla la creación atómica.
    if (activeConv._pending) {
      try {
        const convResponse = await goChatService.createOrGetConversation(
          activeConv._otherUserId,
          text || '',
          productIdAttachment || null,
          reservationIdAttachment || null,
        );
        const convData = convResponse.data.conversation;
        const initialMsg = convResponse.data.message;
        setActiveConv(convData);
        setConversations(prev => [convData, ...prev.filter(r => r.id !== convData.id)]);
        if (initialMsg) setMessages([initialMsg]);
        return;
      } catch (err) {
        // Mostrar el error real del backend si lo tenemos — sino el caller
        // pierde toda info útil para debugging.
        const serverMsg = err?.response?.data?.error || err?.message;
        setError(serverMsg
          ? t('Error al crear la conversación: {{msg}}', { msg: serverMsg })
          : t('Error al crear la conversación'));
        return;
      }
    }

    goChatSocket.sendMessage(activeConv.id, text, productIdAttachment, reservationIdAttachment);

    const optimisticPreview = text
      || (reservationIdAttachment ? t('Reserva compartida') : null)
      || (productIdAttachment ? t('Producto compartido') : '');
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      conversationId: activeConv.id,
      senderId: String(user?.id),
      type: 'TEXT',
      payload: {
        text,
        ...(productIdAttachment ? { productId: String(productIdAttachment) } : {}),
        ...(reservationIdAttachment ? { reservationId: String(reservationIdAttachment) } : {}),
      },
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimisticMessage]);
    setConversations(prev => prev.map(r =>
      r.id === activeConv.id ? { ...r, lastMessage: optimisticPreview, updatedAt: new Date().toISOString() } : r
    ));
  }, [inputText, attachedProductId, attachedReservationId, activeConv, isConnected, user?.id, t]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const newMessagesDividerIndex = useMemo(() => {
    if (!activeConv || messages.length === 0) return -1;
    const myLastReadAt = activeConv.lastReadAt?.[String(user?.id)];
    if (!myLastReadAt) {
      return messages.findIndex(m => String(m.senderId) !== String(user?.id));
    }
    const lastReadDate = new Date(myLastReadAt);
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      if (String(msg.senderId) !== String(user?.id) && new Date(msg.createdAt) > lastReadDate) {
        return i;
      }
    }
    return -1;
  }, [activeConv, messages, user?.id]);

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="max-w-screen-lg mx-auto mt-8 flex justify-center items-center" style={{ height: '60vh' }}>
        <div className="w-8 h-8 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const showConversationList = !isPhoneLayout || !activeConv;
  const showChatArea = !isPhoneLayout || Boolean(activeConv);

  return (
    <div className="w-full h-full flex flex-col bg-[var(--surface)]">
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 shrink-0 mx-3 mt-2">
          <span className="material-symbols-outlined text-[18px]">error</span>
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="opacity-60 hover:opacity-100">
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 flex overflow-hidden bg-[var(--surface)]">
        {showConversationList && (
          <ConversationList
            conversations={conversations}
            activeConv={activeConv}
            getOtherParticipantName={getOtherParticipantName}
            onSelectConversation={openConversation}
            formatDate={formatDate}
          />
        )}

        {showChatArea && (
          <div className="flex flex-col flex-1 min-w-0 min-h-0">
            <MessageThread
              activeConv={activeConv}
              messages={messages}
              currentUserId={user?.id}
              isConnected={isConnected}
              inputText={inputText}
              onInputChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              onSendMessage={handleSendMessage}
              onBack={() => setActiveConv(null)}
              getOtherParticipantName={getOtherParticipantName}
              formatTime={formatTime}
              newMessagesDividerIndex={newMessagesDividerIndex}
              messagesEndRef={messagesEndRef}
              attachedProductId={attachedProductId}
              onClearAttachedProduct={() => setAttachedProductId(null)}
              productCache={productCache}
              attachedReservationId={attachedReservationId}
              onClearAttachedReservation={() => setAttachedReservationId(null)}
              reservationCache={reservationCache}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GoMessages;
